import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';

import { IAdvancedPaymentListItem } from 'src/types/advanced-payment';

import axios from '../../utils/axios';
import { rollbackEmployeeDeletion } from './employees';

interface IInitialState {
  status: 'idle' | 'loading' | 'success' | 'failure';
  error: string | undefined;
  payments: {
    count: number;
    page: number;
    pageSize: number;
    pageCount: number;
    items: IAdvancedPaymentListItem[];
  };
  payment: IAdvancedPaymentListItem | null;
}

const initialState: IInitialState = {
  status: 'idle',
  error: undefined,
  payments: {
    count: 0,
    page: 0,
    pageSize: 0,
    pageCount: 0,
    items: [],
  },
  payment: null,
};

const advancedPaymentsSlice = createSlice({
  name: 'advancedPayments',
  initialState,
  reducers: {
    removeAdvancedPayment: (state, action) => {
      const { paymentID } = action.payload;

      const index = state.payments.items.findIndex((payment) => payment._id === paymentID);

      if (index > -1) {
        action.payload.index = index;
        state.payments.items = state.payments.items.filter((payment) => payment._id !== paymentID);
      }
    },
    rollbackAdvancedPaymentDeletion: (state, action) => {
      const { employee: payment, index } = action.payload;

      state.payments.items.splice(index, 0, payment);
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getPaginatedAdvancedPayments.pending, (state) => {
        state.status = 'loading';
      })
      .addCase(getPaginatedAdvancedPayments.fulfilled, (state, action) => {
        state.status = 'success';
        state.payments = action.payload;
      })
      .addCase(getPaginatedAdvancedPayments.rejected, (state, action) => {
        state.payments = initialState.payments;
        state.status = 'failure';
        state.error = action.error.message;
      })

      .addCase(getAdvancedPaymentByID.pending, (state) => {
        state.status = 'loading';
      })
      .addCase(getAdvancedPaymentByID.fulfilled, (state, action) => {
        state.status = 'success';
        state.payment = action.payload;
      })
      .addCase(getAdvancedPaymentByID.rejected, (state, action) => {
        state.payment = initialState.payment;
        state.status = 'failure';
        state.error = action.error.message;
      });
  },
});

export default advancedPaymentsSlice.reducer;

export const { removeAdvancedPayment, rollbackAdvancedPaymentDeletion } =
  advancedPaymentsSlice.actions;

export const getPaginatedAdvancedPayments = createAsyncThunk(
  'advanced-payments/paginated',
  async (params: {
    page: number | null;
    limit: number | null;
    generalSearch: string | null;
    employee?: string | null;
    startDate?: Date | null;
    endDate?: Date | null;
    sortBy: string | null;
    sort: 'asc' | 'desc' | null;
  }) => {
    try {
      const response = await axios.get(
        `advanced-payment?page=${params.page !== null ? params.page + 1 : ''}&limit=${
          params.limit ?? ''
        }&generalSearch=${params.generalSearch ?? ''}&employee=${params.employee ?? ''}&startDate=${
          params.startDate ?? ''
        }&endDate=${params.endDate ?? ''}&sortBy=${params.sortBy}&sort=${params.sort}`
      );

      return response.data.data;
    } catch (err) {
      console.log(err);
      throw err;
    }
  }
);

export const getFilteredCalculatedAmount = createAsyncThunk(
  'advanced-payments/calculated',
  async (params: {
    generalSearch: string | null;
    employee?: string | null;
    startDate?: Date | null;
    endDate?: Date | null;
  }) => {
    try {
      const response = await axios.get(
        `advanced-payment/total?generalSearch=${params.generalSearch ?? ''}&employee=${
          params.employee ?? ''
        }&startDate=${params.startDate ?? ''}&endDate=${params.endDate ?? ''}`
      );

      return response.data.data;
    } catch (err) {
      console.log(err);
      throw err;
    }
  }
);

export const getAdvancedPaymentByID = createAsyncThunk(
  'employees/getPaymentById',
  async (params: { paymentID: string }) => {
    const { paymentID } = params;

    try {
      const response = await axios.get(`advanced-payment/${paymentID}`);

      return response.data.data;
    } catch (err) {
      console.log(err);
      throw err;
    }
  }
);

export const deleteAdvancedPayment = createAsyncThunk(
  'advanced-payments/deletePayment',
  async (params: { payment: IAdvancedPaymentListItem; index: number }, { dispatch }) => {
    const { payment, index } = params;

    try {
      const response = await axios.delete(`advanced-payment/${payment._id}`);
      return response.data.data;
    } catch (err) {
      dispatch(rollbackEmployeeDeletion({ payment, index }));
      throw err;
    }
  }
);

export interface TCreateAdvancedPayment
  extends Omit<
    IAdvancedPaymentListItem,
    '_id' | 'createdAt' | 'updatedAt' | 'employee' | 'paidDate'
  > {
  date: Date;
}

export const createPayment = createAsyncThunk(
  'advanced-payments/createPayment',
  async (params: { employeeID: string; payment: TCreateAdvancedPayment }) => {
    const { employeeID, payment } = params;

    const response = await axios.post(`advanced-payment/${employeeID}`, payment);

    return response.data.data;
  }
);

export const updatePayment = createAsyncThunk(
  'advanced-payments/updatePayment',
  async (params: { paymentID: string; payment: TCreateAdvancedPayment }) => {
    const { payment, paymentID } = params;
    const response = await axios.patch(`advanced-payment/${paymentID}`, payment);
    return response.data.data;
  }
);
